import { useState, useEffect } from "react";
import type { AnswerEntry, Question } from "../lib/types";

interface Props {
  question: Question;
  index: number;
  total: number;
  existing?: AnswerEntry;
  onAnswer: (entry: AnswerEntry) => void;
  onBack?: () => void;
}

export function QuizQuestion({ question, index, total, existing, onAnswer, onBack }: Props) {
  const isMulti = question.type === "MULTI_SELECT_TILE";
  const [selected, setSelected] = useState<string | null>(existing?.chosenValue ?? null);
  const [picks, setPicks] = useState<string[]>(() => parsePicks(existing?.chosenValue));
  const progress = ((index + 1) / total) * 100;

  useEffect(() => {
    setSelected(existing?.chosenValue ?? null);
    setPicks(parsePicks(existing?.chosenValue));
  }, [question.id, existing?.chosenValue]);

  const submit = () => {
    if (isMulti) {
      const value = picks.join(",");
      // Skippable multi-select can submit empty.
      if (value.length === 0 && !question.skippable) return;
      onAnswer({ questionId: question.id, version: question.version, chosenValue: value });
      return;
    }
    if (selected === null) return;
    onAnswer({ questionId: question.id, version: question.version, chosenValue: selected });
  };

  const togglePick = (value: string) => {
    setPicks((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      const max = question.max_select ?? 8;
      if (prev.length >= max) return prev; // ignore extra picks
      return [...prev, value];
    });
  };

  const submitDisabled = isMulti
    ? picks.length === 0 && !question.skippable
    : selected === null;

  const isLast = total > 1 && index + 1 >= total;
  const submitLabel = isMulti
    ? picks.length === 0 && question.skippable
      ? "跳过 →"
      : isLast ? "看结果 →" : "下一题 →"
    : isLast ? "看结果 →" : "下一题 →";

  return (
    <section className="screen q-screen">
      <div className="progress"><div className="fill" style={{ width: `${progress}%` }} /></div>
      <div className="progress-counter">{index + 1} / {total}</div>
      {onBack && <div className="back-link" onClick={onBack}>← 上一题</div>}

      <div>
        <div className="q-text">{question.text_zh}</div>

        {question.type === "IMAGE_ID" && question.hero_image_url && (
          <div className="q-hero">
            <img src={question.hero_image_url} alt="" />
          </div>
        )}

        {(question.type === "SINGLE_SELECT_TILE" || question.type === "SCENARIO") && (
          <div className="q-tiles">
            {question.options.map((opt) => (
              <button
                key={opt.value}
                className={"tile" + (selected === opt.value ? " selected" : "")}
                onClick={() => setSelected(opt.value)}
              >
                {opt.emoji && <span className="emoji">{opt.emoji}</span>}
                <span>{opt.label_zh}</span>
              </button>
            ))}
          </div>
        )}

        {question.type === "TWO_CARD" && (
          <div className="q-two-card">
            {question.options.map((opt) => (
              <button
                key={opt.value}
                className={"two-card" + (selected === opt.value ? " selected" : "")}
                onClick={() => setSelected(opt.value)}
              >
                {opt.emoji && <span className="two-card-emoji">{opt.emoji}</span>}
                <span className="two-card-label">{opt.label_zh}</span>
              </button>
            ))}
          </div>
        )}

        {(question.type === "SINGLE_SELECT_IMAGE" || question.type === "IMAGE_ID") && (() => {
          const hasImages = question.options.some((o) => o.image_url);
          if (hasImages) {
            return (
              <div className="image-tiles">
                {question.options.map((opt) => (
                  <div
                    key={opt.value}
                    className={"image-tile" + (selected === opt.value ? " selected" : "")}
                    onClick={() => setSelected(opt.value)}
                  >
                    {opt.image_url && <img src={opt.image_url} alt={opt.label_zh} />}
                    <div className="label">{opt.label_zh}</div>
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div className="q-tiles">
              {question.options.map((opt) => (
                <button
                  key={opt.value}
                  className={"tile" + (selected === opt.value ? " selected" : "")}
                  onClick={() => setSelected(opt.value)}
                >
                  {opt.emoji && <span className="tile-emoji">{opt.emoji}</span>}
                  <span>{opt.label_zh}</span>
                </button>
              ))}
            </div>
          );
        })()}

        {question.type === "SLIDER_5" && (
          <SliderInput
            value={selected ?? "2"}
            onChange={setSelected}
            anchors={[
              question.options[0]?.label_zh ?? "",
              question.options[question.options.length - 1]?.label_zh ?? "",
            ]}
          />
        )}

        {question.type === "MULTI_SELECT_TILE" && (
          <>
            <div className="multi-meta">
              {question.max_select && question.max_select < question.options.length
                ? `最多选 ${question.max_select} 个`
                : "可多选"}
              {picks.length > 0 && ` · 已选 ${picks.length}`}
            </div>
            <div className="q-tiles multi">
              {question.options.map((opt) => {
                const checked = picks.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    className={"tile" + (checked ? " selected" : "")}
                    onClick={() => togglePick(opt.value)}
                    aria-pressed={checked}
                  >
                    {opt.emoji && <span className="emoji">{opt.emoji}</span>}
                    <span>{opt.label_zh}</span>
                    {checked && <span className="multi-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="q-bottom">
        <button className="btn btn-primary" disabled={submitDisabled} onClick={submit}>
          {submitLabel}
        </button>
      </div>
    </section>
  );
}

function parsePicks(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(",").filter(Boolean);
}

function SliderInput({
  value,
  onChange,
  anchors,
}: {
  value: string;
  onChange: (v: string) => void;
  anchors: [string, string];
}) {
  const numeric = parseInt(value, 10);
  const safe = Number.isFinite(numeric) ? numeric : 2;
  const pct = (safe / 4) * 100;
  return (
    <div className="slider-row">
      <input
        type="range"
        min={0}
        max={4}
        step={1}
        value={safe}
        className="slider-input"
        style={{ ["--p" as string]: `${pct}%` } as React.CSSProperties}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="slider-anchors">
        <span>{anchors[0]}</span>
        <span>{anchors[1]}</span>
      </div>
    </div>
  );
}
